import React, { useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiGrid, FiX, FiLogOut } from 'react-icons/fi';
import { FaFlag, FaCalculator, FaTimes } from 'react-icons/fa';
import ACCAQuestionArea from './ACCAQuestionArea';
import ShowInstructions from './ShowInstructions';

const ACCAInterface = ({
  showFullScreenQuestion,
  setShowFullScreenQuestion,
  currentQuestion,
  setCurrentQuestion,
  questions,
  currentQuestionData,
  timeLeft,
  examProgress,
  flaggedQuestions,
  toggleQuestionFlag,
  answers,
  handleAnswerSelect,
  handleFillInTheBlankAnswer,
  // Additional props for ACCA modals
  showInstructions,
  toggleInstructions,
  showCalculator,
  setShowCalculator,
  showQuestionNavigator,
  setShowQuestionNavigator,
  exam,
  isAccaExam
}) => {
  // const [showDebugPanel, setShowDebugPanel] = React.useState(true);
  // Show ACCA interface when it's an ACCA exam and showFullScreenQuestion is true
  if (!isAccaExam || !showFullScreenQuestion) {
    console.log('🎯 ACCAInterface not showing:', { isAccaExam, showFullScreenQuestion });
    return null;
  }

  // Skip loading state - start exam immediately
  if (!questions || questions.length === 0) {
    console.log('🎯 ACCAInterface: No questions available, skipping loading state');
    return null;
  }

  // Always hide instructions in ACCA interface
  if (showInstructions) {
    console.log('🎯 ACCAInterface: Forcing showInstructions to false');
    toggleInstructions();
  }

  const isMockExam = typeof exam?.title === 'string' && exam.title.toLowerCase().includes('mock');

  // Copy protection - prevent text selection and copying
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable copy, cut, paste shortcuts
    const handleKeyDown = (e) => {
      // Disable Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+V, Ctrl+S, F12, Ctrl+Shift+I, Ctrl+U
      if (
        (e.ctrlKey && (e.key === 'c' || e.key === 'C' || e.key === 'x' || e.key === 'X' || e.key === 'v' || e.key === 'V' || e.key === 'a' || e.key === 'A' || e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i'))
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // NOTE: ACCA interface no longer manages fullscreen itself.
  // Fullscreen is requested once from the Start Exam button before navigation.

  return (
    <>
      {/* ACCA Full-Screen Question Modal */}
      <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col" style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingRight: 'env(safe-area-inset-right)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)'
      }}>
        {isMockExam && (
          <div className="w-full bg-red-600 text-white text-center text-xs sm:text-sm py-1 px-3">
            This is a Mock Test for practice purposes only — not an official exam.
          </div>
        )}
                {/* Top Header - Black Bar */}
        <div className="bg-black text-white px-5 py-2.5 flex justify-between items-center border-b border-gray-700">
          <button
            onClick={() => setShowFullScreenQuestion(false)}
            className="bg-transparent border-none text-white cursor-pointer flex items-center text-sm"
          >
            <FiChevronLeft size={16} />
            <FiChevronLeft size={16} className="-ml-3" />
            <span className="ml-1">Back</span>
          </button>
          <div></div>
        </div>

        {/* Second Header - Light Grey with Sections */}
        <div className="flex bg-white items-stretch border-b border-gray-300 w-full h-24 gap-[3px]">
          {/* Left Section - ACCA Branding & Question Info */}
          <div className="bg-gray-200 px-6 flex items-center justify-end flex-[2] py-0">
            <div className="flex items-center">
              <div className="bg-red-600 text-white w-15 h-18 flex items-center justify-center font-extrabold text-3xl text-base mr-4">
                ACCA
              </div>
              <div className="flex flex-col h-15 mr-4 justify-between">
                <div className="text-gray-700 text-sm font-bold">
                  Section A
                </div>
                <div className="text-gray-700 text-sm font-bold ">
                  Question {currentQuestion + 1}
                </div>
              </div>
              <div className="text-gray-700 text-sm font-bold self-end">
                2 marks
              </div>
            </div>
          </div>

          {/* Middle Section - Time */}
          <div className="bg-gray-200 px-6 flex items-center flex-[0.5] py-3">
            <div className="text-center w-full h-full flex flex-col justify-between items-center">
              <div className="text-xs text-gray-700 font-bold">
                Time Remaining
              </div>
              <div className="text-base font-bold text-gray-700">
                {Math.floor(timeLeft / 3600)}:{(Math.floor(timeLeft / 60) % 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>

          {/* Progress Section */}
          <div className="bg-gray-200 px-6 flex items-center flex-1 py-0">
            <div className="text-center w-full h-full flex flex-col justify-center items-center">
              {/* Progress Bar with Text Overlay */}
              <div className="w-64 h-6 bg-gray-700 rounded relative overflow-hidden mx-auto">
                <div 
                  className="h-full bg-amber-500 rounded transition-all duration-300"
                  style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    Test {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
                  </span>
                </div>
              </div>
              
              {/* Test Progress Details with Icon and Dropdown */}
              <div className="text-xs text-amber-500 flex items-center justify-center mt-[3px]">
                <FiGrid size={20} className="mr-1" />
                Test Progress Details
                <div className="w-4 h-4 rounded-full bg-gray-700 flex items-center justify-center ml-2">
                  <FiChevronLeft size={12} className="rotate-90 text-amber-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Test Details Section */}
          <div className="bg-gray-200 flex items-center flex-[1.5] py-0">
            <div className="flex flex-col text-left w-full h-full justify-end">
              <div className="text-amber-500 text-base font-semibold">
                {exam?.title || 'FFA-Financial Accounting'}
              </div>
              <div className="text-gray-700 text-md font-bold">
                Sep 24 - Aug 25
              </div>
              <div className="text-gray-700 text-md font-bold">
                Test Number {currentQuestion + 1}
              </div>
            </div>
          </div>

          {/* Exit Section */}
          <div className="bg-gray-200 px-4 flex items-center flex-[0.8]">
            <div className="flex w-full justify-start">
              <div className="flex flex-col items-center">
                <button
                  onClick={() => setShowFullScreenQuestion(false)}
                  className="w-10 h-10 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center cursor-pointer mb-1 hover:opacity-90 active:scale-95"
                >
                  <FiLogOut size={18} className="text-amber-500" />
                </button>
                <div className="text-amber-500 text-sm font-medium">
                  Exit
                </div>
              </div>
            </div>
          </div>
        </div>



        {/* ACCA Question Area - Full Screen - Now using separate component */}
        <ACCAQuestionArea
          questions={questions}
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          currentQuestionData={currentQuestionData}
          answers={answers}
          handleAnswerSelect={handleAnswerSelect}
          handleFillInTheBlankAnswer={handleFillInTheBlankAnswer}
          flaggedQuestions={flaggedQuestions}
          toggleQuestionFlag={toggleQuestionFlag}
          showInstructions={showInstructions}
          toggleInstructions={toggleInstructions}
          showCalculator={showCalculator}
          setShowCalculator={setShowCalculator}
          showQuestionNavigator={showQuestionNavigator}
          setShowQuestionNavigator={setShowQuestionNavigator}
          exam={exam}
        />

        {/* Bottom Footer Bar */}
        <div className="bg-gray-800 text-white px-6 py-4 flex justify-between items-center mx-auto w-full">
          <div className='max-w-5xl mx-auto w-full flex justify-between items-center'>
             {/* Left - Help */}
          <div className="flex flex-col items-center gap-1">
            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
              <span className="text-amber-500 text-lg font-bold">?</span>
            </div>
            <span className="text-amber-500 text-sm font-medium">Help</span>
          </div>

          {/* Right - Navigation */}
          <div className="flex items-center gap-6">
            {/* Flag Question */}
            <button
              onClick={() => toggleQuestionFlag(currentQuestion)}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                <FaFlag size={16} className="text-amber-500" />
              </div>
              <span className="text-amber-500 text-sm">Flag</span>
            </button>

            {/* Previous Question */}
            <button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                <FiChevronLeft size={25} strokeWidth={4} className="text-amber-500" />
              </div>
              <span className="text-amber-500 text-sm">Previous</span>
            </button>

            {/* Next Question */}
            <button
              onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))}
              disabled={currentQuestion === questions.length - 1}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <FiChevronRight size={25} strokeWidth={4} className="text-amber-500" />
              </div>
              <span className="text-amber-500 text-sm">Next</span>
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* ACCA Instructions Modal */}
      {showInstructions && (
        <ShowInstructions
          exam={exam}
          onNext={toggleInstructions}
          onExit={toggleInstructions}
        />
      )}

      {/* ACCA Calculator Modal */}
      {showCalculator && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-3 border-red-600 rounded-none p-5 z-50 min-w-[300px] shadow-2xl">
          <div className="flex justify-between items-center mb-5 border-b-2 border-red-600 pb-3">
            <h3 className="text-xl font-bold text-red-600 m-0 flex items-center gap-2">
              <FaCalculator size={20} />
              🧮 ACCA CALCULATOR
            </h3>
            <button
              onClick={() => setShowCalculator(false)}
              className="bg-red-600 text-white border-none w-8 h-8 rounded-full text-base cursor-pointer font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="text-center p-5">
            <p className="text-gray-600 mb-5">
              Scientific calculator functionality will be implemented here.
            </p>
            <div className="bg-gray-50 border border-gray-300 p-4 rounded font-mono text-lg min-h-[50px] flex items-center justify-center">
              Calculator Display
            </div>
          </div>
        </div>
      )}

      {/* ACCA Question Navigator Modal */}
      {showQuestionNavigator && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-3 border-red-600 rounded-none p-5 z-50 min-w-[400px] max-h-[80vh] overflow-auto shadow-2xl">
          <div className="flex justify-between items-center mb-5 border-b-2 border-red-600 pb-3">
            <h3 className="text-xl font-bold text-red-600 m-0 flex items-center gap-2">
              <FiGrid size={20} />
              🧭 ACCA QUESTION NAVIGATOR
            </h3>
            <button
              onClick={() => setShowQuestionNavigator(false)}
              className="bg-red-600 text-white border-none w-8 h-8 rounded-full text-base cursor-pointer font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))] gap-2.5 p-5">
            {questions.map((question, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentQuestion(index);
                  setShowQuestionNavigator(false);
                }}
                className={`p-2.5 text-sm font-semibold cursor-pointer min-h-[50px] flex items-center justify-center relative rounded border-2 transition-colors ${
                  currentQuestion === index 
                    ? 'bg-red-600 text-white border-red-700' 
                    : answers[question.id] 
                      ? 'bg-green-500 text-white border-green-600'
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                }`}
              >
                {index + 1}
                {flaggedQuestions.has(index) && (
                  <FaFlag 
                    size={12} 
                    className={`absolute top-0.5 right-0.5 ${
                      currentQuestion === index ? 'text-white' : 'text-red-600'
                    }`}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Copy protection styles */}
      <style>{`
        /* Copy protection styles */
        .no-copy {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
          pointer-events: auto;
        }
        
        .no-copy * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        
        /* Allow selection for input fields and textareas */
        .no-copy input,
        .no-copy textarea {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
        }
        
        /* Disable text selection on question content */
        .question-content {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Disable text selection on options */
        .question-options {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        
        /* Allow selection only for input elements within options */
        .question-options input {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
        }
      `}</style>
    </>
  );
};

export default ACCAInterface;
